import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactElement,
} from "react";
import { chromeControlClass } from "../lib/chrome-control.js";
import { cn } from "../lib/utils.js";

export type ChromeIconButtonVariant = "quiet" | "bar";

export type ChromeIconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label"
> & {
  /** The accessible name — required, as on Button `iconOnly`: this control
   *  never carries a visible label. */
  "aria-label": string;
  /** `quiet` (default) is today's muted chrome text (TabStrip New tab,
   *  CardChrome's drag handle and menu trigger). `bar` is the top bar's
   *  utility button (TopBar, NotificationsPopover). */
  variant?: ChromeIconButtonVariant | undefined;
  /** Sets `aria-current="page"` and today's current-page paint (TopBar
   *  Home while its route is current). The caller decides when it applies. */
  current?: boolean | undefined;
};

/**
 * A 32px square chrome icon button (`h-8 w-8`, `rounded-md`), painted from
 * the internal chrome-control recipe (`lib/chrome-control.ts`) shared with
 * `WorkspaceControlButton`. The glyph is the child — the caller sizes it, as
 * with a Button `iconOnly` glyph — and extra children (e.g. a count badge)
 * are allowed. `ref` and every other prop reach the `<button>`, so dnd-kit
 * listeners and `data-tour-id` spread onto it unchanged.
 */
export const ChromeIconButton = forwardRef<
  HTMLButtonElement,
  ChromeIconButtonProps
>(function ChromeIconButton(
  { variant = "quiet", current = false, className, type, children, ...rest },
  ref,
): ReactElement {
  return (
    // eslint-disable-next-line ds/no-hand-built -- this is the design-system part: it paints from lib/chrome-control.ts
    <button
      ref={ref}
      type={type ?? "button"}
      aria-current={current ? "page" : undefined}
      className={cn(chromeControlClass({ look: variant, current }), className)}
      {...rest}
    >
      {children}
    </button>
  );
});
